<script setup lang="ts" generic="T">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
	items: T[]
	itemsPerPage?: number
	loadMoreThreshold?: number
	loadingDelay?: number
}>(), {
  itemsPerPage: 20,
  loadMoreThreshold: 0,
  loadingDelay: 1000,
})

const currentPage = ref(1)
const loadMoreTrigger = ref<HTMLElement | null>(null)
const isLoading = ref(false)
let observer: IntersectionObserver | null = null

const visibleItems = computed(() => {
  const endIndex = currentPage.value * props.itemsPerPage
  return props.items.slice(0, endIndex)
})

const hasMore = computed(() => {
  return visibleItems.value.length < props.items.length
})

async function loadMore() {
  if (isLoading.value || !hasMore.value) return

  isLoading.value = true

  // Show loading animation for a bit
  await new Promise(resolve => setTimeout(resolve, props.loadingDelay))

  currentPage.value++
  isLoading.value = false
}

watch(() => props.items.length, () => {
  currentPage.value = 1
  isLoading.value = false
})

onMounted(() => {
  setTimeout(() => {
    if (!loadMoreTrigger.value) return

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore.value && !isLoading.value) {
            loadMore()
          }
        })
      },
      {
        root: null,
        rootMargin: `${props.loadMoreThreshold}px`,
        threshold: 0.1,
      },
    )

    observer.observe(loadMoreTrigger.value)
  }, 100)
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<template>
  <div class="base-infinite-scroll">
    <slot
      v-for="(item, index) in visibleItems"
      :key="index"
      name="item"
      :item="item"
      :index="index"
    />

    <div
      v-if="hasMore"
      ref="loadMoreTrigger"
      class="flex space-x-1.5 justify-center items-center py-4"
    >
      <span class="loading-dot rounded-full animate-bounce ![animation-delay:-0.25s]" />
      <span class="loading-dot rounded-full animate-bounce ![animation-delay:-0.1s]" />
      <span class="loading-dot rounded-full animate-bounce" />
    </div>
  </div>
</template>

<style scoped>
.base-infinite-scroll {
	width: 100%;
}

.loading-dot {
	@apply h-1.5 w-1.5 text-white bg-white;
}

@keyframes bounce {
	0%, 100% {
		transform: translateY(-35%);
		animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
	}
	50% {
		transform: translateY(0);
		animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}
}

.animate-bounce {
	animation: bounce 0.75s infinite;
}
</style>

